import { useContext, useEffect, useRef } from "react";

import { IconChevronLeft, IconChevronRight, IconChevronsLeft, IconChevronsRight, TablerIcon } from "@tabler/icons";

import { TableContext } from "@sso/context/tableContext";
import { useAppDispatch } from "@sso/hooks/useRedux";
import { Button } from "@sso/ui/Button";
import { Flex } from "@sso/ui/FlexBox";
import { RawInput } from "@sso/ui/RawInput";
import { Typography } from "@sso/ui/Typography";

interface PaginationButtonProps {
  Icon: TablerIcon;
  disabled?: boolean;
  onClick?: () => void;
}

interface TablePaginationProps {
  totalPages?: number;
  actionButtons?: React.ReactNode;
}

const PaginationButton = ({ Icon, onClick, disabled }: PaginationButtonProps) => (
  <Button padding="0.5rem 0.2rem" variant="text" margin="0.2rem 0" disabled={disabled} onClick={onClick}>
    <Icon size={20} />
  </Button>
);

export const TablePagination = ({ totalPages = 1, actionButtons }: TablePaginationProps) => {
  const dispatch = useAppDispatch();
  const { pagination } = useContext(TableContext);
  const inputPageIndexRef = useRef<HTMLInputElement>(null!);

  const { pageIndex, action } = pagination;

  const canPreviousPage = totalPages > 1 && pageIndex > 1;
  const canNextPage = totalPages > pageIndex;

  useEffect(() => {
    if (pageIndex > totalPages && totalPages !== 0) {
      dispatch(action(totalPages));
    }
    // eslint-disable-next-line
  }, [totalPages]);

  const gotoPage = (page: number) => dispatch(action(page));

  const handlePageIndex = () => {
    const page = Number(inputPageIndexRef.current.value);
    if (page > totalPages || page <= 0) {
      return;
    }
    gotoPage(page);
  };

  return (
    <Flex rounded wrap="wrap" width="100%" padding="0.25rem 1rem" margin="auto 0 0 0" bgColor="#c1e4d26e">
      <Flex justify="flex-end" width="65%">
        <Flex margin="0 1.5rem">
          <PaginationButton Icon={IconChevronsLeft} onClick={() => gotoPage(1)} disabled={!canPreviousPage} />
          <PaginationButton
            Icon={IconChevronLeft}
            onClick={() => gotoPage(pageIndex - 1)}
            disabled={!canPreviousPage}
          />
          <Typography overflowEllipsis fontWeight="bold" margin="0 1.5rem">
            {pageIndex} of {totalPages}
          </Typography>
          <PaginationButton Icon={IconChevronRight} onClick={() => gotoPage(pageIndex + 1)} disabled={!canNextPage} />
          <PaginationButton Icon={IconChevronsRight} onClick={() => gotoPage(totalPages)} disabled={!canNextPage} />
        </Flex>
        <Flex>
          <Typography margin="0 0.5rem">Go to page: </Typography>
          <RawInput
            width="60px"
            padding="0.4rem"
            lineHeight={1}
            defaultValue={pageIndex}
            ref={inputPageIndexRef}
            type="number"
            min={1}
            max={totalPages}
          />
          <Button padding="0.5rem 0.2rem" variant="text" margin="0 0.5rem" onClick={handlePageIndex}>
            Go
          </Button>
        </Flex>
      </Flex>
      <Flex justify="flex-end" width="35%">
        {actionButtons && actionButtons}
      </Flex>
    </Flex>
  );
};
